/*!
  Pioneer WFRIA Checkbox v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Checkbox"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Checkbox") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Checkbox");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["name", "aria-label", "className", "checked", "defaultChecked", "disabled", "display", "fieldControlClassName", "iconId", "id", "indeterminate", "label", "labelHidden", "labelLocation", "required", "errorMessage", "leftIcon", "onValueChange", "rightIcon", "decorative", "labelTextClassName", "spacing", "onBlur"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable complexity */
import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { EVENT_TYPES, KEYBOARD_KEYS } from "../common/constants";
import { useFocusFieldOnError, useUniqueId } from "../common/hooks";
import { invalidIf, requiredIf, validIcon } from "../common/proptypes";
import { registerField } from "../common/utilities";
import { FieldsetContext } from "../Fieldset/FieldsetContext";
import { FormContext } from "../Form/FormContext";
import Box from "../private/Box/Box";
import FieldFooter from "../private/FieldFooter/FieldFooter";
import { CHECKBOX_LABEL_CLASS, CHECKBOX_LABEL_TEXT_CLASS } from "../themes/common/classes";
import extractTheme from "./extractTheme";
/**
 *
 * A checkbox is an input for choosing from predefined options:
 * - When used alone, it gives a binary choice (checked/unchecked);
 * - In a group it allows the user to select multiple values from
 *   a list  of options.
 *
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const Checkbox = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    name,
    "aria-label": ariaLabel,
    className,
    checked: controlledChecked,
    defaultChecked,
    disabled,
    display,
    fieldControlClassName,
    iconId,
    id,
    indeterminate,
    label,
    labelHidden,
    labelLocation,
    required,
    errorMessage,
    leftIcon,
    onValueChange,
    rightIcon,
    decorative,
    labelTextClassName,
    spacing,
    onBlur
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssBody,
    cssBodyError,
    cssFooter,
    cssFooterError,
    cssInput,
    cssLabel,
    cssLabelText,
    cssIcon
  } = extractTheme({
    theme,
    labelLocation
  });
  const checkboxRef = useRef();
  const labelRef = useRef();
  const innerContainerRef = useRef();
  const onMountRef = useRef(true);
  const {
    register,
    errors = {},
    isSubmitting,
    resetDone,
    unregister
  } = useContext(FormContext) || {};
  useFocusFieldOnError(checkboxRef, innerContainerRef, isSubmitting);
  const {
    disabled: disabledFromFieldsetContext,
    errorState: errorStateFromFieldsetContext
  } = useContext(FieldsetContext) || {};
  const checkboxId = useUniqueId({
    id,
    prefix: "pnr-checkbox-"
  });
  const errorId = useUniqueId("pnr-checkbox-input-error-");
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked || false);
  const [isDisabled, setIsDisabled] = useState(disabled || disabledFromFieldsetContext);
  const [isIndeterminate, setIsIndeterminate] = useState();
  /**
   * Registering our field with the "register" method from
   * react-hook-form.
   */

  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    requiredErrorMessage: errorMessage
  });
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [name, unregister]);
  /**
   * Keep disabled state current with disabled prop changes.
   */

  useEffect(() => {
    setIsDisabled(disabled || disabledFromFieldsetContext);
  }, [disabled, disabledFromFieldsetContext]);
  /**
   * Listen for changes to enclosing fieldset disabled state.
   */

  useEffect(() => {
    const enclosingFieldset = checkboxRef.current.closest("fieldset");

    if (enclosingFieldset) {
      if (enclosingFieldset.disabled) {
        setIsDisabled(true);
      }

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === "disabled") {
            setIsDisabled(enclosingFieldset.disabled);
          }
        });
      });
      observer.observe(enclosingFieldset, {
        attributes: true
      });
      return () => observer.disconnect();
    }
  }, []);
  /**
   * If an element has been provided for the label prop and an `aria-label`
   * prop has not been provided, replace the aria-labels with the textContent
   * of the label element.
   */

  useEffect(() => {
    if (!ariaLabel && typeof label === "object") {
      const labelText = labelRef.current && labelRef.current.textContent;
      innerContainerRef.current && innerContainerRef.current.setAttribute("aria-label", labelText);
      checkboxRef.current && checkboxRef.current.setAttribute("aria-label", labelText);
    }
  }, [ariaLabel, checkboxRef, label]);
  /**
   * Keep indeterminate state and property of input current with indeterminate prop changes.
   */

  useEffect(() => {
    checkboxRef.current.indeterminate = indeterminate;
    setIsIndeterminate(indeterminate);
  }, [indeterminate]);
  /**
   * Force the `onBlur` event on the actual <input /> in order to validate.
   */

  const handleInnerContainerBlur = event => {
    const {
      relatedTarget
    } = event;

    if (event.target !== checkboxRef.current) {
      // Enable the underlying input to be operated on.
      checkboxRef.current.removeAttribute("hidden");
      checkboxRef.current.style.display = "inline";
      checkboxRef.current.focus();
      checkboxRef.current.blur(); // Return the underlying input to its former state.

      checkboxRef.current.style.display = "none";
      checkboxRef.current.setAttribute("hidden", true);
      relatedTarget && relatedTarget.focus();
    }

    onBlur && onBlur(event);
  };

  const inputChecked = controlledChecked !== null ? controlledChecked : uncontrolledChecked;
  /**
   * Click checkboxRef to notify React Hook Form of change of checked state.
   */

  useEffect(() => {
    // do not set checked values on mount
    if (onMountRef.current) {
      onMountRef.current = false;
    } else {
      checkboxRef.current.click();

      if (controlledChecked !== null) {
        checkboxRef.current.checked = controlledChecked;
      } else {
        checkboxRef.current.checked = uncontrolledChecked;
      }
    }
  }, [uncontrolledChecked, controlledChecked]);
  /**
   * Toggles Checkbox on clicks and keydown events.
   */

  const handleToggle = event => {
    const {
      key,
      type
    } = event;
    const spaceKeyPressed = key === KEYBOARD_KEYS.SPACE;
    const enterPressed = key === KEYBOARD_KEYS.ENTER;
    const clicked = type === EVENT_TYPES.CLICK;
    /**
     * Only clicking, spacebar keydown, and enter keydown should
     * toggle the checkbox. Likewise, if the Checkbox is disabled,
     * exit the function.
     */

    if (isDisabled || !clicked && !spaceKeyPressed && !enterPressed) {
      return;
    } // In the event of `spaceKeyPressed`, prevent the screen from scrolling.


    event.preventDefault();

    if (controlledChecked === null) {
      checkboxRef.current.indeterminate = false;
      setIsIndeterminate(false);
      setUncontrolledChecked(!uncontrolledChecked);
    }

    onValueChange && onValueChange({
      event,
      e: event,
      name,
      id: checkboxId,
      checked: !inputChecked,
      type: "checkbox",
      value: !inputChecked
    });
    event.stopPropagation();
  };
  /*
   * Reset Checkbox on React Hook Form reset.
   */


  useEffect(() => {
    if (resetDone && !controlledChecked) {
      setUncontrolledChecked(defaultChecked || false);
    }
  }, [resetDone, controlledChecked, defaultChecked]);
  /**
   * This method returns the Checkbox and Label text / Menu Item text together.
   */

  const renderCheckboxWithLabel = () => {
    const leftIconElement = _jsx("span", {
      css: cssIcon,
      className: "pnr-checkbox--icon-left",
      id: iconId,
      children: leftIcon
    });

    const labelTextClassNames = clsx(CHECKBOX_LABEL_TEXT_CLASS, labelTextClassName);
    let newLabel = label;

    if (leftIcon) {
      /**
       * Space in front of the label is needed in order to
       * set the aria-label for the Multiselect obtained via node.textContent
       */
      newLabel = ` ${label}`;
    }

    if (rightIcon) {
      /**
       * Space after the label is needed in order to
       * set the aria-label for the Multiselect obtained via node.textContent
       */
      newLabel = `${newLabel} `;
    }

    const labelText = _jsx("span", {
      "aria-hidden": labelLocation !== "left" ? true : null,
      className: labelTextClassNames,
      ref: labelRef,
      css: cssLabelText,
      children: newLabel
    });

    return labelLocation === "left" ? _jsxs("span", {
      className: CHECKBOX_LABEL_CLASS,
      css: cssLabel,
      "aria-hidden": "true",
      children: [leftIcon && leftIconElement, !labelHidden && labelText]
    }) : _jsxs(React.Fragment, {
      children: [_jsx("span", {
        className: CHECKBOX_LABEL_CLASS,
        css: cssLabel
      }), leftIcon && leftIconElement, !labelHidden && labelText]
    });
  };

  const tabIndex = isDisabled ? -1 : 0;
  let fieldFooterProps = null,
      ariaChecked;

  if (errors[name]) {
    fieldFooterProps = {
      displayErrors: true,
      label,
      footerErrorStyle: cssFooterError,
      footerStyle: cssFooter,
      message: errors[name].message,
      id: errorId
    };
  }

  const containerClassNames = clsx(className, "pnr-checkbox", `pnr-checkbox--label-${labelLocation}`);
  const inputClassNames = clsx(fieldControlClassName, "pnr-checkbox--input");

  if (isIndeterminate) {
    ariaChecked = "mixed";
    checkboxRef.current.indeterminate = true;
  } else if (controlledChecked !== null) {
    ariaChecked = controlledChecked;
  } else {
    ariaChecked = uncontrolledChecked;
  }

  return _jsx(Box, {
    spacing: spacing,
    display: display,
    theme: theme,
    children: _jsxs("div", {
      className: containerClassNames,
      css: cssRoot,
      children: [_jsxs("span", _objectSpread(_objectSpread({
        id: checkboxId,
        role: "checkbox",
        css: errors[name] || errorStateFromFieldsetContext ? _objectSpread(_objectSpread({}, cssBody), cssBodyError) : cssBody,
        className: "pnr-checkbox--inner-container",
        "aria-checked": ariaChecked,
        "aria-disabled": isDisabled || false,
        "aria-invalid": errors[name] ? "true" : null,
        "aria-required": required || null,
        "aria-describedby": errors[name] ? errorId : null,
        "aria-label": ariaLabel || label,
        tabIndex: decorative ? null : tabIndex,
        onClick: decorative ? null : handleToggle,
        onBlur: handleInnerContainerBlur,
        onKeyDown: handleToggle
      }, otherProps), {}, {
        ref: innerContainerRef,
        children: [renderCheckboxWithLabel(), _jsx("input", _objectSpread(_objectSpread(_objectSpread({
          /**
           * This is necessary to pacify Axe.
           * We DO NOT expect it to be announced or accessed, since this
           * input is completely hidden from the UI.
           *
           * https://dequeuniversity.com/rules/axe/3.3/checkboxgroup?application=axeAPI
           */
          "aria-label": ariaLabel || label,
          disabled: isDisabled,
          type: "checkbox",
          className: inputClassNames,
          css: cssInput,
          name: name
        }, otherProps), reactHookFormRest), {}, {
          onClick: event => event.stopPropagation(),
          ref: e => {
            /**
             * Normally, react-hook-form does that automatically.
             * In this case, we want to share the ref with the user, so
             * we need to run this ourselves.
             *
             * See "How to share ref usage?" in the FAQs.
             * https://react-hook-form.com/faqs
             */
            inputRef && inputRef(e);
            checkboxRef.current = e;

            if (userRef) {
              userRef.current = e;
            }
          },
          required: required,
          defaultChecked: inputChecked,
          hidden: true
        })), rightIcon && _jsx("span", {
          className: "pnr-checkbox--icon pnr-checkbox--icon-right",
          id: iconId,
          children: rightIcon
        })]
      })), _jsx(FieldFooter, _objectSpread({}, fieldFooterProps))]
    })
  });
});
Checkbox.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Unique name identifying this Checkbox within a form. */
  name: PropTypes.string.isRequired,

  /** Checkbox value for use in controlled component. */
  checked: PropTypes.bool,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /**
   * Determines whether the Checkbox is to be used just for decorative purposes.
   * This is currently used for MultiSelect component.
   */
  decorative: PropTypes.bool,

  /** For setting initial checked state.  */
  defaultChecked: PropTypes.bool,

  /** Disable or enable the look of a Checkbox. */
  disabled: PropTypes.bool,

  /** CSS display type for enclosing Box */
  display: PropTypes.string,

  /**
   * The error message that will be displayed above or below the checkbox.
   * This `errorMessage` prop is required if the `required` prop is set to true.
   */
  errorMessage: requiredIf(PropTypes.string, (props, propName) => props.required && !props[propName], "if the `required` prop is `true`."),

  /** CSS class to apply to the HTML input field. */
  fieldControlClassName: PropTypes.string,

  /** Id for Checkbox icon (left or right). */
  iconId: PropTypes.string,

  /** Checkbox element `id` value. While optional, if an `id` is not added, one will be generated. */
  id: PropTypes.string,

  /** The indeterminate state for Checkbox */
  indeterminate: PropTypes.bool,

  /**
   * Checkbox label. The label may be excluded as long as the pass-thru prop `aria-label`
   * is used. This will not show a label to sighted users, yet it will provide a value
   * for screen readers.
   */
  label: invalidIf(PropTypes.oneOfType([PropTypes.string, PropTypes.node]), (props, propName) => props[propName] === null && !props["aria-label"], `label must be used if aria-label is not present.`),

  /** Visually hides the label, but allows for a label to exist for assistive technology. */
  labelHidden: PropTypes.bool,

  /** Location where the label text renders in relation to the checkbox. */
  labelLocation: PropTypes.oneOf(["left", "right"]),

  /**
   * Used to apply the CSS for label.
   */
  labelTextClassName: PropTypes.string,

  /**
   * A React component of type Icon to be placed on the left.
   * This is currently used for MutliSelect component to display MenuItem with a Checkbox.
   */
  leftIcon: validIcon,

  /**
   * Callback to be executed on value change of the checkbox.
   * The onValueChange function differs from onChange in that it receives an object
   * with the following signature as a parameter: {value}. The value will be a boolean.
   *
   * @param {Object} object signature {value}
   */
  onValueChange: PropTypes.func,

  /**
   * If set to `true`, Checkbox validity would remain in error state
   * until the Checkbox is "checked".
   * This prop is only applicable if customValidators prop is
   * not set for the Fieldset component when Checkbox is contained within it.
   */
  required: PropTypes.bool,

  /**
   * A React component of type Icon to be placed on the right.
   * This is currently used for MutliSelect component to display MenuItem with a Checkbox.
   */
  rightIcon: validIcon,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()])
} : {};
Checkbox.defaultProps = {
  className: "",
  defaultChecked: null,
  disabled: false,
  display: "blocK",
  fieldControlClassName: "",
  iconId: null,
  id: null,
  indeterminate: false,
  label: null,
  labelHidden: false,
  labelLocation: "right",
  onValueChange: null,
  checked: null,
  required: false,
  errorMessage: null,
  leftIcon: null,
  rightIcon: null,
  decorative: false,
  labelTextClassName: null,
  spacing: {}
};
Checkbox.displayName = "Checkbox";
export default Checkbox;